import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
  HarmonizedViolationsQuery,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import {
  formatDaysToResolution,
  formatDiscoveryQuarter,
  formatViolationDate,
  isViolationStale,
  PROGRAM_HEADER_HINT,
  ProgramChip,
  SEVERITY_HEADER_HINT,
  SeverityChip,
  STALE_HEADER_HINT,
  StatusChip,
} from "./violationDisplay";

export type ViolationRow = HarmonizedViolationsQuery["harmonizedViolations"]["items"][number];

type AssigneeUser = NonNullable<ViolationRow["assignee"]>;

/** Display an assignee as their person full name, falling back to email. */
export function assigneeDisplayName(user: AssigneeUser): string {
  return getFullName(user.person) || user.email;
}

/**
 * Columns depend on the latest refresh start (for the stale flag), so this is
 * a factory called inside a useMemo in the page rather than a module constant.
 *
 * IMPORTANT: program/severity/status are synthetic filter columns whose `field`
 * equals the Omnisearch filter key, NOT a Prisma column — they MUST be
 * sortable:false (the API would otherwise orderBy a non-existent column).
 */
export function buildViolationColumns(
  refreshStartedAt: string | null | undefined,
  isStaff: boolean
): OmnisearchGridColDef<ViolationRow>[] {
  const staffColumns: OmnisearchGridColDef<ViolationRow>[] = isStaff
    ? [
        {
          field: "staffAssignee",
          headerName: "Staff assignee",
          description:
            "Encamp staff responsible party. Falls back to the facility default when not set on the violation. Visible to staff only.",
          flex: 0.35,
          sortable: false,
          renderCell: ({ row }) =>
            row.staffAssignee ? (
              <Stack sx={{ minWidth: 0, py: 0.5 }}>
                <Typography variant="body2" noWrap title={assigneeDisplayName(row.staffAssignee)}>
                  {assigneeDisplayName(row.staffAssignee)}
                </Typography>
                {row.staffAssigneeIsFacilityDefault ? (
                  <Typography variant="caption" color="text.secondary">
                    (facility default)
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              "—"
            ),
        },
      ]
    : [];

  return [
    {
      field: "facility",
      headerName: "Facility",
      flex: 0.6,
      filterKeyType: "facility",
      valueGetter: (_value, row) => row.facility.name,
    },
    {
      field: "assignee",
      headerName: "Assignee",
      description:
        "Responsible party. Falls back to the facility default when not set on the violation.",
      flex: 0.35,
      sortable: false,
      renderCell: ({ row }) =>
        row.assignee ? (
          <Stack sx={{ minWidth: 0, py: 0.5 }}>
            <Typography variant="body2" noWrap title={assigneeDisplayName(row.assignee)}>
              {assigneeDisplayName(row.assignee)}
            </Typography>
            {row.assigneeIsFacilityDefault ? (
              <Typography variant="caption" color="text.secondary">
                (facility default)
              </Typography>
            ) : null}
          </Stack>
        ) : (
          "—"
        ),
    },
    ...staffColumns,
    {
      field: "program",
      headerName: "Program",
      description: PROGRAM_HEADER_HINT,
      flex: 0.42,
      sortable: false,
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationProgram),
      renderCell: ({ row }) => <ProgramChip program={row.sourceProgram} />,
    },
    {
      field: "severity",
      headerName: "Severity",
      description: SEVERITY_HEADER_HINT,
      flex: 0.3,
      sortable: false,
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationSeverity),
      renderCell: ({ row }) => <SeverityChip severity={row.standardizedSeverity} />,
    },
    {
      field: "status",
      headerName: "Reported status",
      description:
        "Resolution status as reported in the source government data (EPA/state). Read-only — track your own resolution with the 'Resolution status' column.",
      flex: 0.5,
      sortable: false,
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationResolutionStatus),
      renderCell: ({ row }) => <StatusChip status={row.standardizedResolutionStatus} />,
    },
    {
      field: "violationDescription",
      headerName: "Violation",
      flex: 0.8,
      renderCell: ({ row }) => (
        <Stack sx={{ minWidth: 0, py: 0.5 }}>
          <Typography variant="body2" noWrap title={row.violationDescription ?? ""}>
            {row.violationDescription ?? "—"}
          </Typography>
          {row.violationCode ? (
            <Typography variant="caption" color="text.secondary">
              {row.violationCode}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "violationStartDate",
      headerName: "Started",
      flex: 0.3,
      renderCell: ({ row }) => formatViolationDate(row.violationStartDate),
    },
    {
      field: "lastSeenAt",
      headerName: "Last seen",
      description: STALE_HEADER_HINT,
      flex: 0.35,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {formatViolationDate(row.lastSeenAt)}
          {isViolationStale(row.lastSeenAt, refreshStartedAt) ? (
            <Chip size="small" color="warning" variant="outlined" label="Stale" />
          ) : null}
        </Box>
      ),
    },
    {
      field: "violationAgency",
      headerName: "Agency",
      description: "Agency that recorded the violation.",
      flex: 0.3,
    },
    { field: "citations", headerName: "Regulatory citation", flex: 0.4 },
    { field: "pollutantTypeDesc", headerName: "Pollutant", flex: 0.3 },
    {
      field: "stdSeverityDeterminantDesc",
      headerName: "Severity reason",
      description: "Why this severity was assigned (e.g. EPA Significant Non-Compliance).",
      flex: 0.3,
    },
    { field: "epaFacilityId", headerName: "EPA facility ID", flex: 0.25 },
    { field: "registryId", headerName: "Registry ID", flex: 0.25 },
    { field: "state", headerName: "State", flex: 0.15 },
    { field: "sourceType", headerName: "Source type", flex: 0.25 },
    {
      field: "violationEndDate",
      headerName: "Resolution date",
      flex: 0.25,
      renderCell: ({ row }) => formatViolationDate(row.violationEndDate),
    },
    {
      field: "daysToResolution",
      headerName: "Days to resolution",
      description: "Calendar days from violation start to resolution date.",
      flex: 0.25,
      sortable: false,
      valueGetter: (_v: unknown, row: ViolationRow) =>
        formatDaysToResolution(row.violationStartDate, row.violationEndDate),
    },
    {
      // Read-only here; the resolution status is edited in the violation detail
      // drawer. Backed by the `govStatus` field (user-controlled, preserved
      // across data refreshes — distinct from EPA's derived status column).
      field: "govStatus",
      headerName: "Resolution status",
      description:
        "User-tracked resolution status, preserved across data refreshes. Editable in the violation's detail drawer.",
      flex: 0.3,
      valueGetter: (_v: unknown, row: ViolationRow) => row.govStatus ?? "",
      renderCell: ({ row }) => (row.govStatus ? <StatusChip status={row.govStatus} /> : "—"),
    },
    {
      // Not sortable: the value is a derived "YYYY Q#" string with no backing
      // Prisma column, and this grid sorts server-side.
      field: "quarterDiscovered",
      headerName: "Quarter discovered",
      description: "Quarter the violation started.",
      flex: 0.25,
      sortable: false,
      valueGetter: (_v: unknown, row: ViolationRow) =>
        formatDiscoveryQuarter(row.violationStartDate),
    },
  ];
}

// Focused default-visible set; every other column is available via manage-columns.
// `facility` is the leftmost column and stays visible.
export const VIOLATION_DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  facility: true,
  program: true,
  severity: true,
  status: true,
  violationDescription: true,
  assignee: true,
  // Staff-visible by default when present (the column is only built for staff).
  staffAssignee: true,
  govStatus: true,
  violationStartDate: true,
  // everything else hidden by default:
  lastSeenAt: false,
  violationAgency: false,
  citations: false,
  pollutantTypeDesc: false,
  stdSeverityDeterminantDesc: false,
  epaFacilityId: false,
  registryId: false,
  state: false,
  sourceType: false,
  violationEndDate: false,
  daysToResolution: false,
  quarterDiscovered: false,
};
